import { useEffect, useState } from "react";
import QuestionBox from "../../ui/QuestionBox/QuestionBox";
import RadioButton from "../../ui/RadioButton/RadioButton";
import classes from "../Infrastructure/Infrastructure.module.css";
import QuestionCard from "../../ui/QuestionCard/QuestionCard";
import CommonFooter from "../../CommonFooter/CommonFooter";
import axios from "axios";
import { useAuth } from "../../../context/AuthContext";
import { env } from "../../../constants/env";
import { Bounce, toast } from "react-toastify";
import { useParams } from "react-router-dom";
import CommonButton from "../../ui/CommonButton/CommonButton";

const initialState = {
  biosafetyLevel: "",
  biosafetyCabinetClass: "",
  biosafetyCabinetCount: "",
  isBiosafetyCabinetCertified: "",
  lastCabinetCertificationDate: "",
  hasBiosafetyOfficer: "",
  biosafetyOfficerName: "",
  officerTrained: "",
  hasControlledAccess: "",
  hasFireExtinguishers: "",
  fireExtinguishingMechanisms: ["", ""],
  hasSpillKits: "",
};

const Biosafety = () => {
  const [currentSet, setCurrentSet] = useState(0);
  const [answers, setAnswers] = useState(["", ""]);
  const { user } = useAuth();

  const { labId: paramLabId } = useParams();
  const labId = paramLabId || user?.labId;

  const userRole = user?.role;

  const [formData, setFormData] = useState(initialState);
  const [ownerLabId, setOwnerLabId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isfetching, setIsFetching] = useState(false);

  const isReadOnly = userRole !== "LABORATORY" || !editMode;

  const sectionName = "biosafety"; // formType

  useEffect(() => {
    if (labId) {
      fetchQuestionnaire();
    }
  }, [labId]);

  const fetchQuestionnaire = async () => {
    try {
      setIsFetching(true);
      const res = await axios.get(
        `${env.kataApiUrl}/api/questionnaire/${labId}/${sectionName}`
      );

      if (res.data) {
        if (res.data.answers) {
          setFormData(res.data.answers);
          setEditMode(false); // ✅ answers exist → read-only by default
        } else {
          setFormData(initialState);
          setEditMode(true); // ✅ no answers → open in edit mode
        }
        setOwnerLabId(res.data.labId);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        console.log("No existing questionnaire found for this lab.");
        setFormData(initialState);
        setEditMode(true); // ✅ new lab → start in edit mode
      } else {
        console.error("Error fetching questionnaire:", error);
        toast.error("Failed to load saved questionnaire");
      }
    } finally {
      setIsFetching(false);
    }
  };

  // --- pass disabled to inputs ---
  const handleChange = (field, value) => {
    if (isReadOnly) return; // block edits
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNestedChange = (section, key, value) => {
    if (isReadOnly) return;
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }));
  };

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet(currentSet + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet(currentSet - 1);
  };

  const handleDone = async () => {
    try {
      setLoading(true);
      const payload = {
        labId,
        formType: sectionName,
        answers: formData,
      };

      const res = await axios.post(
        `${env.kataApiUrl}/api/questionnaire/${sectionName}`,
        payload
      );

      toast.success("Submitted successfully!");

      fetchQuestionnaire();
      setCurrentSet(0);
      setEditMode(false); // exit edit mode

      console.log(res.data);
    } catch (error) {
      console.error(error);
      toast.error("Submission failed");
    } finally {
      setLoading(false);
    }
  };

  const questionSets = [
    {
      content: (
        <div className="d-flex flex-column gap-4">
          <QuestionBox
            number={1}
            question="What is the Biosafety Level of the Laboratory?"
            multilineInput
            value={formData.biosafetyLevel}
            onChange={(e) => handleChange("biosafetyLevel", e.target.value)}
            disabled={isReadOnly}
          />
          <QuestionBox
            number={2}
            question="What is the Class of Biosafety Cabinet in use?"
            multilineInput
            value={formData.biosafetyCabinetClass}
            onChange={(e) =>
              handleChange("biosafetyCabinetClass", e.target.value)
            }
            disabled={isReadOnly}
          />
          <QuestionBox
            number={3}
            question="How many Biosafety Cabinet available?"
            disabled={isReadOnly}
          >
            <div
              className={`${classes.customInput}  d-flex align-items-center`}
            >
              <input
                type="text"
                placeholder="Enter number"
                onChange={(e) =>
                  handleChange("biosafetyCabinetCount", e.target.value)
                }
                value={formData.biosafetyCabinetCount}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={4}
            question="Is/Are the Biosafety Cabinet(s) Certified?"
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="isBiosafetyCabinetCertified"
                checked={formData.isBiosafetyCabinetCertified === "Yes"}
                onChange={() =>
                  handleChange("isBiosafetyCabinetCertified", "Yes")
                }
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="isBiosafetyCabinetCertified"
                checked={formData.isBiosafetyCabinetCertified === "No"}
                onChange={() =>
                  handleChange("isBiosafetyCabinetCertified", "No")
                }
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={5}
            question="When was the last Biosafety Cabinet Certification?"
            disabled={isReadOnly}
          >
            <div className={`${classes.customInput} d-flex align-items-center`}>
              <input
                type="text"
                placeholder="Enter date"
                onChange={(e) =>
                  handleChange("lastCabinetCertificationDate", e.target.value)
                }
                value={formData.lastCabinetCertificationDate}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={6}
            question="Does the Laboratory have a Biosafety Officer?"
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="hasBiosafetyOfficer"
                checked={formData.hasBiosafetyOfficer === "Yes"}
                onChange={() => handleChange("hasBiosafetyOfficer", "Yes")}
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="hasBiosafetyOfficer"
                checked={formData.hasBiosafetyOfficer === "No"}
                onChange={() => handleChange("hasBiosafetyOfficer", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={7}
            question="Has the Biosafety Officer undergone Biosafety Training?"
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="officerTrained"
                checked={formData.officerTrained === "Yes"}
                onChange={() => handleChange("officerTrained", "Yes")}
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="officerTrained"
                checked={formData.officerTrained === "No"}
                onChange={() => handleChange("officerTrained", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={8}
            question="Name of the Biosafety Officer?"
            disabled={isReadOnly}
          >
            <div
              className={`${classes.customInput}  d-flex align-items-center`}
            >
              <input
                type="text"
                placeholder="Enter name"
                onChange={(e) =>
                  handleChange("biosafetyOfficerName", e.target.value)
                }
                value={formData.biosafetyOfficerName}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={9}
            question="Does the Laboratory have a controlled Access?"
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="hasControlledAccess"
                checked={formData.hasControlledAccess === "Yes"}
                onChange={() => handleChange("hasControlledAccess", "Yes")}
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="hasControlledAccess"
                checked={formData.hasControlledAccess === "No"}
                onChange={() => handleChange("hasControlledAccess", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={10}
            question="Does the Laboratory have fire extinguishers?"
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="hasFireExtinguishers"
                checked={formData.hasFireExtinguishers === "Yes"}
                onChange={() => handleChange("hasFireExtinguishers", "Yes")}
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="hasFireExtinguishers"
                checked={formData.hasFireExtinguishers === "No"}
                onChange={() => handleChange("hasFireExtinguishers", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
          <QuestionBox
            number={11}
            question="List any mechanisms the Laboratory has put in place to extinguish fires?"
            singleInput
            answers={formData.fireExtinguishingMechanisms}
            onAnswersChange={(newAnswers) =>
              handleChange("fireExtinguishingMechanisms", newAnswers)
            }
            disabled={isReadOnly}
          />
          <QuestionBox
            number={12}
            question="Does the Laboratory have any spill kits for spillage control?"
            disabled={isReadOnly}
          >
            <div className="d-flex gap-2 mb-2">
              <RadioButton
                label="Yes"
                name="hasSpillKits"
                checked={formData.hasSpillKits === "Yes"}
                onChange={() => handleChange("hasSpillKits", "Yes")}
                disabled={isReadOnly}
              />
              <RadioButton
                label="No"
                name="hasSpillKits"
                checked={formData.hasSpillKits === "No"}
                onChange={() => handleChange("hasSpillKits", "No")}
                disabled={isReadOnly}
              />
            </div>
          </QuestionBox>
        </div>
      ),
    },
  ];

  const { subtitle, content } = questionSets[currentSet];

  if (isfetching) {
    return <div className={classes.loading}>Loading...</div>;
  }

  return (
    <div
      className={`${classes.wrapper} ${
        paramLabId ? classes.fromParams : ""
      } ${isReadOnly ? classes.readOnly : ""}`}
    >
      <div className="d-flex justify-content-between align-items-center">
        <h2>Biosafety and biosecurity</h2>
        {userRole === "LABORATORY" && !editMode && (
          <CommonButton
            className={classes.editButton}
            onClick={() => setEditMode(true)}
            title="Edit"
          />
        )}
      </div>
      {(!editMode && userRole !== "MINISTRY")   ? (
        <div className={classes.infoText}>
          ✅ Your biosafety and biosecurity has been submitted.
        </div>
      ) : (
        content
      )}
      {!isReadOnly && (
        <CommonFooter
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          handleDone={handleDone}
          cancelText="Cancel"
          handleCancel={() => {
            setEditMode(false);
            fetchQuestionnaire();
            setCurrentSet(0);
          }}
          questionSets={questionSets}
          currentSet={currentSet}
          loading={loading}
          doneText={loading ? "Submitting..." : "Submit"}
        />
      )}
    </div>
  );
};

export default Biosafety;
